import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Megaphone, Image as ImageIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PageHeader, EmptyState, Loading, StatusBadge } from "@/components/common";
import { FormDialog } from "@/components/FormDialog";
import { api, useFetch } from "@/lib/api";
import { useCurrency } from "@/context/AuthContext";
import { ListingPerformance } from "@/components/ListingPerformance";

export const STATUS_LABEL = { draft: "Draft", available: "Live", under_offer: "Under offer", let_agreed: "Let agreed", sold_stc: "Sold STC", withdrawn: "Withdrawn" };
export const QUAL_LABEL = { pcm: "pcm", pw: "pw", asking: "", offers_over: "Offers over", guide: "Guide price", fixed: "Fixed price", oieo: "OIEO", poa: "POA" };

export function priceText(x, cur) {
  if (x.price_qualifier === "poa") return "Price on application";
  const p = new Intl.NumberFormat("en-GB", { style: "currency", currency: cur || "GBP", maximumFractionDigits: 0 }).format(Number(x.price || 0));
  if (x.channel === "letting") return `${p} ${QUAL_LABEL[x.price_qualifier] || "pcm"}`;
  return ["offers_over", "guide", "fixed", "oieo"].includes(x.price_qualifier) ? `${QUAL_LABEL[x.price_qualifier]} ${p}` : p;
}

export default function Listings() {
  const cur = useCurrency();
  const nav = useNavigate();
  const { data } = useFetch("/biz/listings");
  const { data: props } = useFetch("/biz/properties");
  const [open, setOpen] = useState(false);
  return (
    <div data-testid="listings-page">
      <PageHeader eyebrow="Marketing" title="Listings" subtitle="Advertise properties to let or sell on your website, Rightmove and Zoopla."
        actions={<>
          <Link to="/properties" target="_blank" className="inline-flex items-center px-4 h-10 rounded-md border border-slate-300 text-sm font-medium hover:bg-white" data-testid="view-public-listings">View public page</Link>
          <Button onClick={() => setOpen(true)} disabled={!props?.length} className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="add-listing-button"><Plus className="w-4 h-4 mr-1" />New listing</Button>
        </>} />
      {data?.length > 0 && <ListingPerformance />}
      {!data ? <Loading /> : data.length === 0 ? (
        <EmptyState icon={Megaphone} title="No listings yet" text="Create a listing from one of your properties, add photos and publish it." />
      ) : (
        <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-6">
          {data.map((x) => {
            const cover = [...(x.media || [])].sort((a, b) => a.order - b.order).find((m) => m.kind === "photo");
            return (
              <Link key={x.id} to={`/app/listings/${x.id}`} className="bg-white border border-slate-200 rounded-xl overflow-hidden lift" data-testid={`listing-card-${x.id}`}>
                <div className="aspect-[16/9] bg-slate-100 flex items-center justify-center overflow-hidden">
                  {cover && ["available", "under_offer", "let_agreed", "sold_stc"].includes(x.status) ? <img src={`${process.env.REACT_APP_BACKEND_URL}/api/public/media/${cover.id}`} alt="" className="w-full h-full object-cover" />
                    : <ImageIcon className="w-8 h-8 text-slate-300" />}
                </div>
                <div className="p-5">
                  <div className="flex justify-between gap-2"><h3 className="font-bold leading-tight">{x.title}</h3><StatusBadge status={x.status === "available" ? "active" : x.status} label={STATUS_LABEL[x.status]} /></div>
                  <div className="text-sm text-slate-500 mt-1">{x.property_name} · {x.channel === "letting" ? "To let" : "For sale"}</div>
                  <div className="font-display text-lg font-extrabold mt-2">{priceText(x, cur)}</div>
                  <div className="flex flex-wrap gap-2 mt-3 text-xs text-slate-500">
                    {x.portals?.rightmove && <span className="px-2 py-0.5 rounded bg-slate-100 whitespace-nowrap">Rightmove · {(x.portal_status?.rightmove?.state || "off").replace("_", " ")}</span>}
                    {x.portals?.zoopla && <span className="px-2 py-0.5 rounded bg-slate-100 whitespace-nowrap">Zoopla · {(x.portal_status?.zoopla?.state || "off").replace("_", " ")}</span>}
                    <span className="ml-auto whitespace-nowrap" data-testid={`listing-counts-${x.id}`}>{x.views} views · {x.enquiries} {x.enquiries === 1 ? "enquiry" : "enquiries"} · {x.viewings} viewings</span>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      )}
      <FormDialog open={open} onOpenChange={setOpen} title="New listing" testid="new-listing-form" submitLabel="Create draft"
        fields={[
          { name: "property_id", label: "Property", type: "select", required: true, full: true, options: (props || []).map((p) => ({ value: p.id, label: p.name })) },
          { name: "channel", label: "Listing type", type: "select", default: "letting", full: true, options: [{ value: "letting", label: "To let" }, { value: "sale", label: "For sale" }] },
        ]}
        onSubmit={async (v) => {
          const p = props.find((x) => x.id === v.property_id);
          const typeMap = { house: "terraced", flat: "flat", hmo: "hmo_room", bungalow: "bungalow", commercial: "other", other: "other" };
          const r = await api.post("/biz/listings", {
            property_id: p.id, channel: v.channel, title: p.name, display_address: [p.address_line?.split(",").slice(-1)[0], p.city].filter(Boolean).join(", "),
            property_type: typeMap[p.property_type] || "other", price_qualifier: v.channel === "letting" ? "pcm" : "asking",
          });
          toast.success("Draft created");
          nav(`/app/listings/${r.data.id}`);
        }} />
    </div>
  );
}
